import moment from "moment";
import {
  EPFO_EXIT_CODES,
  codeOfReason,
  sampleCodes,
  sampleExitMapping,
} from "pages/PayRoll/Configuration/StatutoryConfiguration/PF/pfConfigData";
import { ageOn } from "pages/PayRoll/Statutory/PF/pfMembersData";
import { codeForLocation, pfSettings, sampleMembers } from "./pfReportsSampleMembers";

// Payroll > Reports > Statutory Reports > PF (screens P7–P12a), on sample
// data until the PF APIs exist. Reports read the finalised payroll
// snapshot only; here the snapshot is worked out once from the PF Members
// and PF Configuration samples (B5 calculation, ROUND half up).

export const PAYROLL_MONTHS = [
  { value: "2026-10", label: "Oct 2026", finalized: true, finalizedAt: "2026-11-01T14:15:00", version: "1.0", paidOn: "2026-11-01" },
  { value: "2026-09", label: "Sep 2026", finalized: true, finalizedAt: "2026-10-01T11:40:00", version: "1.0", paidOn: "2026-10-01" },
  { value: "2026-11", label: "Nov 2026", finalized: false },
];

export const CODES = sampleCodes();
export const codeLabel = (code) => (code ? `${code.type === "MAIN" ? "Main" : "Extension"} · ${code.code}` : "Not linked");
export const codeShort = (code) => (code ? (code.type === "MAIN" ? "Main" : "Ext") : "—");

export const money = (value) => `₹${Math.round(Number(value) || 0).toLocaleString("en-IN")}`;
export const num = (value) => Math.round(Number(value) || 0).toLocaleString("en-IN");
export const formatDay = (value) => (value ? moment(value).format("DD MMM YYYY") : "—");

// Gross earnings and unpaid days of the finalised payroll (sample).
const GROSS = { E001: 42000, E002: 22500, E003: 16000, E004: 12500, E009: 15000, E010: 18900, E117: 36000 };
const UNPAID = { E003: 2, E010: 1 };

// One employee's PF for the month (B5 steps 3–8).
const calculate = (member, settings) => {
  const pfWage = member.pf_wage;
  const epfWage = member.full_salary ? pfWage : Math.min(pfWage, settings.ceiling);
  const age = ageOn(member.dob, moment("2026-10-01"));
  const pension = member.eps_member === "YES" && age != null && age < settings.stopAge;
  const epsWage = pension ? Math.min(epfWage, settings.epsCap) : 0;
  const edliWage = Math.min(pfWage, settings.ceiling);
  const eePf = Math.round(epfWage * settings.eeRate);
  const vpf = Number(member.extra_amount) || 0;
  const erEps = Math.round(epsWage * settings.epsRate);
  const erEpf = Math.round(epfWage * settings.erRate) - erEps;
  return {
    gross: GROSS[member.id] || Math.round(pfWage * 1.6),
    pf_wage: pfWage,
    epf_wage: epfWage,
    eps_wage: epsWage,
    edli_wage: edliWage,
    ee_pf: eePf,
    vpf,
    er_eps: erEps,
    er_epf: erEpf,
    edli: Math.round(edliWage * 0.005),
    admin: Math.round(epfWage * 0.005),
    unpaid_days: UNPAID[member.id] || 0,
    total: eePf + vpf + erEps + erEpf,
  };
};

export const buildRegister = () => {
  const settings = pfSettings();
  return sampleMembers()
    .filter((member) => member.status === "Member")
    .map((member) => {
      const code = codeForLocation(member.location);
      const tags = [];
      if (member.full_salary) tags.push({ label: "full salary", tone: "purple" });
      const age = ageOn(member.dob, moment("2026-10-01"));
      if (member.eps_member === "YES" && age != null && age >= settings.stopAge) tags.push({ label: `age ${settings.stopAge}`, tone: "slate" });
      if (member.eps_member === "NO") tags.push({ label: "no pension", tone: "slate" });
      return {
        ...member,
        ...calculate(member, settings),
        emp_id: member.id,
        code_id: code?.id ?? null,
        code_label: codeShort(code),
        uan_label: member.uan || "To be generated",
        tags,
      };
    });
};

// Payable per challan account for one code (B5 table).
export const accountsFor = (rows) => {
  const sum = (key) => rows.reduce((total, row) => total + row[key], 0);
  const a1 = sum("ee_pf") + sum("vpf") + sum("er_epf");
  const a10 = sum("er_eps");
  const a2 = sum("admin");
  const a21 = sum("edli");
  return { a1, a10, a2, a21, total: a1 + a10 + a2 + a21 };
};
export const ACCOUNTS = [
  { key: "a1", label: "A/c 1", what: "Employee PF + employer EPF", short: "A/c 1 · PF" },
  { key: "a10", label: "A/c 10", what: "Pension (EPS)", short: "A/c 10 · Pension" },
  { key: "a2", label: "A/c 2", what: "Admin charges", short: "A/c 2 · Admin" },
  { key: "a21", label: "A/c 21", what: "EDLI", short: "A/c 21 · EDLI" },
];

// ---- Validation (B7.6 rules that apply to the sample) ----

export const SEVERITY_COLORS = { Blocking: "#DC2626", Warning: "#F0954A", Info: "#2563EB" };

export const buildIssues = (register, exits, mapping) => {
  const issues = [];
  const add = (row, issue) => issues.push({ id: `${row.id}-${issue.rule}`, name: row.name, emp_id: row.id, uan: row.uan, code_id: row.code_id, code_label: row.code_label, ...issue });
  register.forEach((row) => {
    if (!row.uan) {
      add(row, {
        rule: "UAN_MISSING",
        severity: "Blocking",
        issue: `UAN missing — joined ${formatDay(row.joined_pf)}`,
        affects: `${row.code_label} ECR file`,
        fix_in: "PF Members",
        why: "Every line of the ECR file needs the member's 12-digit UAN.",
        effect: `${row.code_label} ECR file for Oct 2026 cannot be generated`,
        steps: [["Create the UAN", "EPFO portal › Member › Register individual"], ["Add the UAN", "Statutory › PF › Members or Import UAN"], ["Generate the file", "ECR File tab"]],
      });
    }
    if (!row.dob) {
      add(row, {
        rule: "DOB_MISSING",
        severity: "Blocking",
        issue: "Date of birth missing — pension cannot be worked out",
        affects: `${row.code_label} ECR file`,
        fix_in: "Employee profile",
        why: "Pension stops at 58, so Cauver needs the date of birth to work out pension",
        effect: `${row.code_label} ECR file for Oct 2026 cannot be generated`,
        steps: [["Add date of birth", "People › Employee profile › Personal"], ["Re-check", "Re-check now, or it runs by itself on save"], ["Generate the file", "ECR File tab"]],
      });
    }
    if (row.tags.some((tag) => tag.label.startsWith("age"))) {
      add(row, {
        rule: "STOP_AGE",
        severity: "Info",
        issue: "Turned 58 — pension stopped, full employer share goes to PF",
        affects: "—",
        fix_in: "—",
        why: "Pension (EPS) stops at the stop age set in PF Configuration.",
        effect: "EPS wages and pension are 0 in the ECR; the employer share goes to employer PF",
        steps: [["Nothing to fix", "For information only"]],
      });
    }
    if (row.eps_member === "NO") {
      add(row, {
        rule: "NOT_EPS",
        severity: "Info",
        issue: "Not a pension member — joined after Sep 2014 above the ceiling",
        affects: "—",
        fix_in: "—",
        why: "Members who joined after 01 Sep 2014 with a PF wage above the ceiling are not in the pension scheme.",
        effect: "EPS wages and pension are 0 in the ECR",
        steps: [["Nothing to fix", "If this is wrong, re-import the row with Pension (EPS) member = Yes"]],
      });
    }
  });
  exits
    .filter((exit) => !codeOfReason(mapping, exit.exit_reason))
    .forEach((exit) => {
      add(exit, {
        rule: "EXIT_UNMAPPED",
        severity: "Warning",
        issue: `Exit reason “${exit.exit_reason}” has no EPFO code`,
        affects: "Exits",
        fix_in: "Exit Codes",
        why: "The EPFO portal needs a reason-for-leaving code to mark the exit.",
        effect: "The exit can't be marked as done until the reason is mapped",
        steps: [["Map the reason", "Configuration › Statutory › PF › Exit Codes"], ["Mark the exit", "Exits tab"]],
      });
    });
  const order = { Blocking: 0, Warning: 1, Info: 2 };
  return issues.sort((a, b) => order[a.severity] - order[b.severity]);
};

// ---- ECR 2.0 (B7.3) ----

const ecrName = (name) => String(name).toUpperCase().replace(/[^A-Z. ]/g, "").slice(0, 85);
export const ecrLine = (row) =>
  [row.uan, ecrName(row.name), row.gross, row.epf_wage, row.eps_wage, row.edli_wage, row.ee_pf + row.vpf, row.er_eps, row.er_epf, row.unpaid_days, 0]
    .map((value) => String(value))
    .join("#~#");
export const ecrFileName = (code, month) => `ECR_${code.code}_${month}.txt`;

export const sampleEcrFiles = () => [
  { id: 1, code_id: 2, month: "2026-10", version: 1, lines: 31, generated_at: "2026-11-05T09:30:00", status: "Current" },
  { id: 2, code_id: 1, month: "2026-09", version: 1, lines: 117, generated_at: "2026-10-04T10:05:00", status: "Current" },
  { id: 3, code_id: 2, month: "2026-09", version: 1, lines: 30, generated_at: "2026-10-04T10:12:00", status: "Current" },
];

// ---- Exits (B7.4) ----

export const EXIT_STATUS_COLORS = { "Marked on portal": "#12B76A", "To mark": "#F0954A", "Fix mapping": "#DC2626" };

export const sampleExits = () => [
  { id: "E015", name: "Karthik S", department: "Production", uan: "100789012399", code_id: 1, joined_pf: "2021-06-01", exit_date: "2026-10-10", exit_reason: "Resignation", marked_on: "2026-11-03", remarks: "", marked_by: "Sadheesh" },
  { id: "E021", name: "Mani K", department: "Stores", uan: "100789012411", code_id: 2, joined_pf: "2008-03-15", exit_date: "2026-10-31", exit_reason: "Superannuation (age 58)", marked_on: "", remarks: "", marked_by: "" },
  { id: "E030", name: "Ramesh P", department: "Production", uan: "100789012422", code_id: 1, joined_pf: "2023-02-01", exit_date: "2026-10-20", exit_reason: "Transfer to group company", marked_on: "", remarks: "", marked_by: "" },
];
export const sampleMapping = sampleExitMapping;

export const exitRow = (exit, mapping) => {
  const code = CODES.find((item) => item.id === exit.code_id);
  const epfo = codeOfReason(mapping, exit.exit_reason);
  const epfoLabel = epfo ? EPFO_EXIT_CODES.find((item) => item.code === epfo)?.label.replace(/\s*\(.*\)$/, "") : "";
  return {
    ...exit,
    emp_id: exit.id,
    code_id: exit.code_id,
    code_label: codeShort(code),
    epfo_code: epfo,
    epfo_label: epfo ? `${epfo} · ${epfoLabel}` : "Not mapped",
    reason_label: exit.exit_reason.replace(/\s*\(.*\)$/, ""),
    status: !epfo ? "Fix mapping" : exit.marked_on ? "Marked on portal" : "To mark",
  };
};

// ---- Reconciliation (B7.5) ----

export const RECON_STATUS_COLORS = { "Not recorded": "#6B7280", "Partly paid": "#F0954A", Paid: "#12B76A", Overpaid: "#2563EB" };

// Payable for earlier months is fixed in the sample; the open month's comes
// from the register.
export const SEPT_PAYABLE = {
  1: { a1: 195500, a10: 97600, a2: 5900, a21: 5900 },
  2: { a1: 51300, a10: 25500, a2: 1500, a21: 1500 },
};

export const sampleChallans = () => [
  { id: 1, code_id: 2, month: "2026-10", kind: "Original", trrn: "2041611003456", paid_on: "2026-11-06", receipt: "challan_ext_oct26.pdf", amounts: null, remarks: "" },
  { id: 2, code_id: 1, month: "2026-09", kind: "Original", trrn: "2041610009876", paid_on: "2026-10-12", receipt: "challan_main_sep26.pdf", amounts: { a1: 195100, a10: 97600, a2: 5900, a21: 5900 }, remarks: "" },
  { id: 3, code_id: 2, month: "2026-09", kind: "Original", trrn: "2041610009877", paid_on: "2026-10-12", receipt: "", amounts: { ...SEPT_PAYABLE[2] }, remarks: "" },
];

export const challanTotal = (challan) => ACCOUNTS.reduce((total, account) => total + (Number(challan.amounts?.[account.key]) || 0), 0);

export const reconStatus = (payable, paid) => {
  if (!paid) return "Not recorded";
  if (paid < payable) return "Partly paid";
  if (paid > payable) return "Overpaid";
  return "Paid";
};

export const dueDate = (month, day = 15) => moment(`${month}-01`).add(1, "month").date(day);

export const EPFO_CODES = EPFO_EXIT_CODES;
